/*
 * Section-type metadata shared by the section list, the add menu and the inline
 * editor. Pure data and pure functions only -- no React -- so it stays cheap to
 * import and to unit test.
 *
 * CREATABLE_TYPES mirrors the backend SectionType enum and validate_section_data:
 * all twelve types can be created and have their data updated through the API.
 * A section whose type is not in this list (one written straight to Firestore by
 * a tool that predates the enum) still opens, in a raw-JSON fallback.
 */

/* Grouped for the add menu; the flat list is derived so the two cannot drift. */
export const TYPE_GROUPS = [
  { label: 'Content', types: ['markdown', 'list_item', 'qa', 'quote', 'html'] },
  { label: 'Media', types: ['image', 'gallery', 'audio', 'youtube', 'embed'] },
  { label: 'Data', types: ['dataset'] },
  { label: 'Layout', types: ['divider'] },
];

export const CREATABLE_TYPES = TYPE_GROUPS.reduce((all, g) => all.concat(g.types), []);

export const TYPE_LABELS = {
  markdown: 'Markdown',
  image: 'Image',
  youtube: 'YouTube',
  html: 'HTML',
  gallery: 'Gallery',
  quote: 'Quote',
  divider: 'Divider',
  embed: 'Embed',
  list_item: 'List item',
  qa: 'Q&A',
  dataset: 'Dataset',
  audio: 'Audio',
};

export const TYPE_HINTS = {
  markdown: 'Prose in Markdown',
  image: 'One picture with a caption',
  youtube: 'Embedded YouTube video',
  html: 'Raw HTML block',
  gallery: 'A grid of images',
  quote: 'Pull quote with attribution',
  divider: 'Horizontal rule or spacer',
  embed: 'Third-party embed code',
  list_item: 'One entry of a “top N” list',
  qa: 'A question and its answer (FAQ markup)',
  dataset: 'A table of figures, optionally charted',
  audio: 'Podcast episode or video by URL',
};

export const typeLabel = (type) => TYPE_LABELS[type] || type || 'Section';

/* True for a type the API does not know: the editor shows the JSON fallback. */
export const isJsonEditedType = (type) => !CREATABLE_TYPES.includes(type);

export function getDefaultSectionData(type) {
  switch (type) {
    case 'markdown':
      return { content: '', allow_html: false };
    case 'image':
      return { media_id: '', src: '', alt: '', caption: '', alignment: 'center', size: 'medium' };
    case 'youtube':
      return { video_id: '', title: '', autoplay: false, start_time: 0, aspect_ratio: '16:9' };
    case 'html':
      return { content: '', sanitize: true };
    case 'gallery':
      return { images: [], layout: 'grid', columns: 3 };
    case 'quote':
      return { quote: '', author: '', source: '', alignment: 'center' };
    case 'divider':
      return { style: 'line', thickness: 'normal', color: '' };
    case 'embed':
      return { embed_code: '', source: '', width: '100%', height: 'auto' };
    case 'list_item':
      return { title: '', introduction: '', description: '', image: null, link: null, position: null, is_ordered: false };
    case 'qa':
      return { question: '', answer: '', header_level: 'h2' };
    case 'dataset':
      // rows are stored wrapped -- Firestore will not nest an array in an array.
      return { title: '', description: '', body: '', column_headers: [], rows: [], to_plot: [], show_table: true, show_graph: true, footnote: '' };
    case 'audio':
      return { url: '', title: '', media_type: 'audio', description: '', duration: '', transcript: '' };
    default:
      return {};
  }
}

const clip = (text, n) => {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/* One line of text summarising a section for its collapsed header. */
export function getSectionPreview(section) {
  const { type, data = {} } = section || {};
  const d = data || {};
  switch (type) {
    case 'markdown':
      return clip(d.content, 80) || 'Empty markdown';
    case 'image':
      return d.alt || clip(d.src, 40) || 'No image yet';
    case 'youtube':
      return d.title || d.video_id || 'No video';
    case 'html':
      return clip(String(d.content || '').replace(/<[^>]*>/g, ''), 80) || 'Empty HTML';
    case 'gallery': {
      const n = (d.images || []).length;
      return count(n, 'image', 'images');
    }
    case 'quote':
      return d.quote ? `“${clip(d.quote, 60)}”` : 'Empty quote';
    case 'divider':
      return `${d.style || 'line'} divider`;
    case 'embed':
      return d.source || clip(d.embed_code, 40) || 'Generic embed';
    case 'list_item': {
      const title = d.title || clip(d.introduction, 60);
      if (!title) return 'Untitled list item';
      return d.is_ordered && d.position ? `${d.position}. ${title}` : title;
    }
    case 'qa':
      return d.question ? clip(d.question, 80) : 'Untitled question';
    case 'dataset': {
      const rows = count((d.rows || []).length, 'row', 'rows');
      return d.title ? `${d.title} — ${rows}` : rows;
    }
    case 'audio': {
      const kind = String(d.media_type || 'audio').toLowerCase() === 'video' ? 'Video' : 'Audio';
      const name = d.title || clip(d.url, 50);
      return name ? `${kind}: ${name}` : `${kind} with no URL`;
    }
    default: {
      // A type the API does not know (something wrote it straight to Firestore).
      // Summarise whatever text it holds rather than saying nothing useful --
      // the header already names the type next to this line.
      const first = Object.keys(d).map((k) => d[k]).find((v) => typeof v === 'string' && v.trim());
      return clip(first, 60) || 'No data';
    }
  }
}
